import React, { useEffect, useState } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { adminApiService } from '@/shared/services/adminApiService';
import { logger, dateUtils } from '@/shared/utils';

type ErrorReport = {
  id: string;
  event_type: 'client_error' | 'server_error';
  description: string;
  metadata: Record<string, any>;
  user_id: string | null;
  ip_address: string | null;
  created_at: string | null;
};

const AdminErrorsPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const [errors, setErrors] = useState<ErrorReport[]>([]);
  const [summary, setSummary] = useState<{ client_errors_24h: number; server_errors_24h: number } | null>(null);
  const [source, setSource] = useState<'all' | 'client_error' | 'server_error'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const loadErrors = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApiService.getErrorReports({
          limit: 100,
          source: source === 'all' ? undefined : source,
        });
        if (response.success && response.data) {
          setErrors(response.data.errors);
          setSummary(response.data.summary);
        } else {
          setError(response.error?.message || 'Failed to load error reports');
          logger.error('Failed to load error reports', 'AdminErrorsPage', { error: response.error });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load error reports');
        logger.error('Network error loading error reports', 'AdminErrorsPage', { error: err });
      } finally {
        setLoading(false);
      }
    };

    loadErrors();
  }, [source]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className={`${theme.card} rounded-2xl p-12 text-center`}>
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
          <p className={theme.textSecondary}>Loading error reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className={`text-2xl font-bold mb-1 ${theme.textHeading} tracking-tight`}>Error Monitoring</h2>
        <p className={theme.textSecondary}>Unhandled errors reported from the frontend and backend, newest first.</p>
      </div>

      {error && (
        <div className={`${theme.alertError} rounded-xl p-4`}>
          <p className={theme.textError}>{error}</p>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className={`rounded-2xl p-6 ${theme.card}`}>
            <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Frontend errors, last 24h</h3>
            <p className={`text-3xl font-bold ${summary.client_errors_24h > 0 ? theme.textError : theme.textHeading} tracking-tight`}>
              {summary.client_errors_24h.toLocaleString()}
            </p>
          </div>
          <div className={`rounded-2xl p-6 ${theme.card}`}>
            <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Backend errors, last 24h</h3>
            <p className={`text-3xl font-bold ${summary.server_errors_24h > 0 ? theme.textError : theme.textHeading} tracking-tight`}>
              {summary.server_errors_24h.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {([
          { id: 'all', label: 'All' },
          { id: 'client_error', label: 'Frontend' },
          { id: 'server_error', label: 'Backend' },
        ] as const).map((filterOption) => (
          <button
            key={filterOption.id}
            onClick={() => setSource(filterOption.id)}
            className={`px-4 py-2 rounded-xl transition capitalize ${
              source === filterOption.id
                ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
                : `${theme.buttonSecondary} ${theme.textSecondary}`
            }`}
          >
            <span className={source === filterOption.id ? theme.textPrimary : theme.textSecondary}>
              {filterOption.label}
            </span>
          </button>
        ))}
      </div>

      <div className={`${theme.card} rounded-3xl p-6`}>
        <h3 className={`text-xl font-semibold mb-4 ${theme.textHeading}`}>Recent Errors</h3>

        {errors.length === 0 ? (
          <p className={`text-center py-8 ${theme.textMuted}`}>No errors reported. Clean slate.</p>
        ) : (
          <div className="space-y-3">
            {errors.map((err) => {
              const isExpanded = expandedId === err.id;
              const [headline, ...rest] = err.description.split('\n\n');
              return (
                <div
                  key={err.id}
                  className={`p-4 rounded-lg border border-opacity-10 transition-all duration-300 ${theme.textPrimary}`}
                >
                  <button
                    className="w-full text-left"
                    onClick={() => setExpandedId(isExpanded ? null : err.id)}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                              err.event_type === 'server_error' ? theme.alertError : theme.alertWarning
                            }`}
                          >
                            {err.event_type === 'server_error' ? 'Backend' : 'Frontend'}
                          </span>
                          <span className={`text-xs ${theme.textMuted}`}>
                            {err.created_at ? dateUtils.format(err.created_at, 'LONG') : 'Unknown time'}
                          </span>
                        </div>
                        <p className={`font-medium truncate ${theme.textHeading}`}>{headline}</p>
                      </div>
                      <span className={`text-xs shrink-0 ${theme.textMuted}`}>{isExpanded ? 'Hide' : 'Details'}</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className={`mt-3 pt-3 border-t border-opacity-10 space-y-2 text-sm ${theme.textSecondary}`}>
                      {rest.length > 0 && (
                        <pre className={`whitespace-pre-wrap break-words text-xs ${theme.textMuted}`}>
                          {rest.join('\n\n')}
                        </pre>
                      )}
                      <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs ${theme.textMuted}`}>
                        {err.ip_address && <span>IP: {err.ip_address}</span>}
                        {err.user_id && <span>User: {err.user_id}</span>}
                        {err.metadata?.level && <span>Level: {err.metadata.level}</span>}
                        {err.metadata?.user_agent && <span>Agent: {err.metadata.user_agent}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminErrorsPage;
